interface FilterState {
  maxPrice: number;
  stops: number[];
  airlines: string[];
  departureTimes: string[];
}

interface FilterSidebarProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  availableAirlines: string[];
  priceRange: [number, number];
}

const STOP_OPTIONS = [
  { label: "Non-stop", value: 0 },
  { label: "1 Stop", value: 1 },
  { label: "2+ Stops", value: 2 },
];

const DEPARTURE_TIMES = [
  { label: "Early Morning", sub: "12:00 AM – 6:00 AM", value: "early" },
  { label: "Morning", sub: "6:00 AM – 12:00 PM", value: "morning" },
  { label: "Afternoon", sub: "12:00 PM – 6:00 PM", value: "afternoon" },
  { label: "Evening", sub: "6:00 PM – 12:00 AM", value: "evening" },
];

function formatPKR(v: number) {
  return `PKR ${v.toLocaleString()}`;
}

export default function FilterSidebar({ filters, onChange, availableAirlines, priceRange }: FilterSidebarProps) {
  const toggle = <T,>(arr: T[], val: T): T[] =>
    arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  return (
    <aside className="w-64 flex-shrink-0">
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden sticky top-24">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800">Filters</h3>
          <button
            onClick={() => onChange({ maxPrice: priceRange[1], stops: [], airlines: [], departureTimes: [] })}
            className="text-xs text-[#0c2461] font-medium cursor-pointer hover:underline whitespace-nowrap"
          >
            Reset All
          </button>
        </div>

        {/* Price Range */}
        <div className="px-4 py-4 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Max Price</p>
          <input
            type="range"
            min={priceRange[0]}
            max={priceRange[1]}
            step={500}
            value={filters.maxPrice}
            onChange={e => onChange({ ...filters, maxPrice: Number(e.target.value) })}
            className="w-full accent-[#0c2461] cursor-pointer"
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-400">{formatPKR(priceRange[0])}</span>
            <span className="text-xs font-semibold text-[#0c2461]">{formatPKR(filters.maxPrice)}</span>
          </div>
        </div>

        {/* Stops */}
        <div className="px-4 py-4 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Stops</p>
          <div className="space-y-2">
            {STOP_OPTIONS.map(opt => (
              <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.stops.includes(opt.value)}
                  onChange={() => onChange({ ...filters, stops: toggle(filters.stops, opt.value) })}
                  className="w-3.5 h-3.5 accent-[#0c2461] cursor-pointer"
                />
                <span className="text-sm text-gray-700 group-hover:text-[#0c2461] transition-colors">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Airlines */}
        <div className="px-4 py-4 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Airlines</p>
          <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
            {availableAirlines.map(airline => (
              <label key={airline} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.airlines.includes(airline)}
                  onChange={() => onChange({ ...filters, airlines: toggle(filters.airlines, airline) })}
                  className="w-3.5 h-3.5 accent-[#0c2461] cursor-pointer"
                />
                <span className="text-xs text-gray-700 group-hover:text-[#0c2461] transition-colors leading-tight">{airline}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Departure Time */}
        <div className="px-4 py-4">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Departure Time</p>
          <div className="grid grid-cols-2 gap-2">
            {DEPARTURE_TIMES.map(dt => (
              <button
                key={dt.value}
                onClick={() => onChange({ ...filters, departureTimes: toggle(filters.departureTimes, dt.value) })}
                className={`px-2 py-2 rounded-lg border text-center cursor-pointer transition-all ${
                  filters.departureTimes.includes(dt.value)
                    ? "border-[#0c2461] bg-[#0c2461]/5 text-[#0c2461]"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <p className="text-xs font-semibold leading-tight">{dt.label}</p>
                <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{dt.sub}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
